import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ParentDashboard() {
  const [children, setChildren] = useState([]);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parent/children");
      setChildren(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const link = async (e) => {
    e.preventDefault();
    try {
      await api.post("/parent/link", { code: code.toUpperCase().trim() });
      toast.success("Dítě bylo úspěšně spárováno.");
      setCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neplatný kód");
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 animate-fade-in">
      <p className="caption mb-6">Rodičovský přehled</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-ink leading-tight">
        Klidná <span className="font-serif-italic">podpora na dálku</span>.
      </h1>
      <p className="text-slate mt-4 max-w-prose text-lg">
        Vidíte agregovaný pokrok, slabá místa a doporučení, jak pomoct.
        Obsah jednotlivých odpovědí zůstává jen u žáka.
      </p>

      {/* Pair child */}
      <section className="mt-14 border-l-2 border-navy pl-6 py-2 max-w-xl">
        <p className="caption mb-2">Přidat dítě</p>
        <p className="text-sm text-slate mb-4">
          Požádejte své dítě o pozvánkový kód, který najde na svém přehledu.
        </p>
        <form onSubmit={link} className="flex gap-3" data-testid="link-child-form">
          <div className="flex-1 space-y-2">
            <Label htmlFor="invite" className="caption">Pozvánkový kód</Label>
            <Input id="invite" value={code} onChange={(e) => setCode(e.target.value)}
              required minLength={6}
              data-testid="invite-input"
              className="bg-bone border-stone uppercase font-mono tabular" placeholder="XXXX-XXXX" />
          </div>
          <Button type="submit" className="self-end bg-navy hover:bg-navy-dark text-parchment rounded-sm py-5"
            data-testid="link-child-btn">
            Spárovat
          </Button>
        </form>
      </section>

      {/* Children list */}
      <section className="mt-16">
        <h2 className="font-serif text-2xl text-ink mb-8">Děti</h2>
        {loading ? (
          <p className="caption">Načítání…</p>
        ) : children.length === 0 ? (
          <p className="font-serif-italic text-slate text-lg">
            Zatím nemáte přidané žádné dítě. Použijte pozvánkový kód výše.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {children.map((c) => (
              <div key={c.user_id} className="bg-bone border border-stone rounded-sm p-8" data-testid={`child-${c.user_id}`}>
                <p className="caption mb-2">
                  {c.grade_target === "ss_4" ? "9. třída → SŠ" : c.grade_target === "gym_6" ? "7. třída → 6leté G" : "5. třída → 8leté G"}
                </p>
                <h3 className="font-serif text-2xl text-ink mb-6">{c.first_name} {c.last_name}</h3>

                <div className="grid grid-cols-2 gap-px bg-stone border border-stone mb-6">
                  <div className="bg-bone px-4 py-4">
                    <p className="caption mb-1">Dokončeno</p>
                    <p className="font-mono tabular text-xl">{c.completed_tests}</p>
                  </div>
                  <div className="bg-bone px-4 py-4">
                    <p className="caption mb-1">Průměr</p>
                    <p className="font-mono tabular text-xl">{c.average_pct}%</p>
                  </div>
                </div>

                <div>
                  <p className="caption mb-3">Kde investovat čas</p>
                  {c.weak_topics.length === 0 ? (
                    <p className="text-sm text-slate">Žádná slabá místa nejsou zatím patrná.</p>
                  ) : (
                    <ul className="space-y-2">
                      {c.weak_topics.map((w, i) => (
                        <li key={i} className="flex justify-between text-sm">
                          <span className="capitalize">{w.topic.replaceAll("_"," ")}</span>
                          <span className="font-mono tabular text-burgundy">{w.accuracy}%</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {c.last_active && (
                  <p className="caption mt-6">Naposled aktivní: {new Date(c.last_active).toLocaleDateString("cs-CZ")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
